import ContentPage from '../../components/aoraki/content-page/content-page-container'
import { useEffect, useState } from 'react'
import {
  APP_NAME,
  UNAUTHENTICATED_MAIN_MENU_LINKS,
  FOOTER_MENU_LINKS,
  FOOTER_LOGOS,
  APP_LOGO,
} from '../../../constants'
import useLogout from '../../hooks/use-logout'
import useUser from '../../hooks/use-user'
import { isValidToken } from '../../services/auth-utils'
import { ContentPageFooter } from '../../components/aoraki/content-page/content-page-footer'
import { useCheckUserToken } from '../../hooks/use-check-user-token'
import { ButtonWithIcon, ButtonWithoutIcon, ContentBlock, PageSkeleton, useTitleChange } from '@msaf/core-react'

export default function TermsOfUse() {
  useTitleChange('Terms of use')
  const { logout } = useLogout()
  const { user, isLoading } = useUser()
  const [headerActions, setHeaderActions] = useState<Array<ButtonWithIcon | ButtonWithoutIcon>>()

  useEffect(() => {
    let actions: Array<ButtonWithIcon | ButtonWithoutIcon> | undefined = undefined
    if (isValidToken(user?.token)) {
      actions = [
        {
          label: 'Logout',
          onClick: () => logout(),
          icon: 'exit',
          iconAlignment: 'left',
          buttonStyle: 'secondary',
        },
      ]
    } else {
      actions = [
        {
          label: 'Register',
          path: '/?register=true',
          buttonStyle: 'primary',
        },
        {
          label: 'Sign in',
          path: '/?login=true',
          buttonStyle: 'secondary',
        },
      ]
    }
    setHeaderActions(actions)
    // Excluded 'logout' function from dependency list to prevent
    // triggering multiple re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useCheckUserToken()

  if (isLoading) {
    return <PageSkeleton />
  }

  // If user is already logged in, they should be redirected
  if (isValidToken(user?.token)) {
    return null
  }

  return (
    <ContentPage menuLinks={UNAUTHENTICATED_MAIN_MENU_LINKS} headerActions={headerActions} appLogo={APP_LOGO}>
      <ContentBlock heading='Terms of Use'>
        <>
          <p>
            Wells Aotearoa New Zealand contains information on well and gallery installations within New Zealand. Its
            primary purpose is to support Regional Councils and Unitary Authorities deliver statutory functions required
            by the Resource Management Act 1991. Well and gallery installation information is also a valuable resource
            for other parties. Wells Aotearoa New Zealand makes this information available as a public service. Please
            take the time to read the Terms and Conditions for the use of Wells Aotearoa New Zealand and the information
            provided.
          </p>
          <ol>
            <li>
              Wells Aotearoa New Zealand and the information provided in it are the copyright of the following Regional
              Councils and Unitary Authorities: Auckland Council, Waikato Regional Council, Environment Canterbury
              Regional Council and Otago Regional Council. Wells Aotearoa New Zealand is a public service and may be
              used for personal and business purposes. However, you are not permitted to copy or republish any
              substantial amount of the information from Wells Aotearoa New Zealand without the prior written consent of
              the aforementioned Regional Councils and Unitary Authorities that have provided the information being
              copied or republished.
            </li>
            <li>
              Copyright in Wells Aotearoa New Zealand is licenced for re-use under a Creative Commons Attribution 4.0
              International licence.
            </li>
            <li>
              Under the terms of that licence you must attribute your use of the information to the Regional Council or
              Unitary Authority that has provided the information.
            </li>
            <li>
              Information within Wells Aotearoa New Zealand has been derived from various sources, including the
              aforementioned Regional Councils and Unitary Authorities databases. The information is subject to change
              and there is no guarantee that the information you are seeking will be available at all times.
            </li>
            <li>
              Whilst due care has been taken in providing Wells Aotearoa New Zealand and the information, all
              information should be considered as being illustrative and indicative only. Your use of information from
              Wells Aotearoa New Zealand is entirely at your own risk.
            </li>
            <li>
              The aforementioned Regional Councils and Unitary Authorities do not give and expressly disclaim any
              warranty as to the accuracy or completeness of the information or its fitness for any purpose.
            </li>
            <li>
              The aforementioned Regional Councils and Unitary Authorities will not be liable for any loss, liability,
              damage, cost or expense suffered or incurred by any person as a result of use, reference to or reliance on
              this information provided by Wells Aotearoa New Zealand.
            </li>
            <li>
              The aforementioned Regional Councils and Unitary Authorities accept no liability for any error, omission,
              or inaccuracy of the information provided by Wells Aotearoa New Zealand.
            </li>
            <li>
              You should independently verify the accuracy of any information before taking any action in reliance upon
              it, i.e. the actual locations of any installations shown on a plan should be physically verified on site
              prior to any use of the information or other works taking place.
            </li>
            <li>
              You waive and release the aforementioned Regional Councils and Unitary Authorities from any claims arising
              from your use of Wells Aotearoa New Zealand or the information provided by it.
            </li>
            <li>
              You indemnify the aforementioned Councils against all claims, loss or damages arising in connection with
              your use of Wells Aotearoa New Zealand or the information provided by Wells Aotearoa New Zealand.
            </li>
            <li>
              You understand that the aforementioned Regional Councils and Unitary Authorities may for security and
              technical reasons at any time in its sole discretion and without prior notice to you, prohibit or restrict
              your access to the Wells Aotearoa New Zealand.
            </li>
            <li>
              You acknowledge that your use of Wells Aotearoa New Zealand may be subject to interruption or delay. The
              aforementioned Regional Councils and Unitary Authorities do not make any warranty that the Wells Aotearoa
              New Zealand will be error free, without interruption or delay or free from defects in design or
              engineering.
            </li>
            <li>
              The laws of New Zealand govern the use of Wells Aotearoa New Zealand and by using it you irrevocably and
              unconditionally submit to the exclusive jurisdiction of the Courts of New Zealand.
            </li>
            <li>
              A copy of these Terms of Use must accompany any information taken from Wells Aotearoa New Zealand which is
              made available to third parties.
            </li>
          </ol>
        </>
      </ContentBlock>

      <ContentPageFooter appName={APP_NAME} menuLinks={FOOTER_MENU_LINKS} logos={FOOTER_LOGOS} />
    </ContentPage>
  )
}
